// src/pages/ModelInfo.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  SparklesIcon,
  ServerIcon,
  CpuChipIcon,
  ArrowLeftIcon,
  ChartBarSquareIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/solid';

const modelStats = [
  {
    title: 'Data Pipeline',
    progress: 40,
    description: 'Live OHLCV & order‑book ingestion',
    icon: ServerIcon,
    color: 'from-pink-500 to-red-500',
    id: 'pipeline'
  },
  {
    title: 'Baseline Model',
    progress: 55,
    description: 'LightGBM at ~55% accuracy',
    icon: CpuChipIcon,
    color: 'from-violet-500 to-pink-500',
    id: 'baseline'
  },
  {
    title: 'Deep Learning',
    progress: 30,
    description: 'LSTM POC training in progress',
    icon: SparklesIcon,
    color: 'from-indigo-500 to-violet-500',
    id: 'deeplearning'
  },
];

export default function ModelInfo() {
  const [activeCard, setActiveCard] = useState(null);

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900 to-black -mt-16 pt-4 pb-8 px-4 text-white font-mono">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center">
            Prediction Model Status
          </h1>
          <p className="mt-3 text-sm text-gray-400 text-center">
            Live overview of model development, progress & next steps.
          </p>
        </div>

        {/* Status Panel - Similar to User Identity Panel in Profile */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-6">
          <div className="flex flex-col md:flex-row items-center gap-6 p-6">
            {/* Model Icon */}
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full opacity-75 blur-sm"></div>
              <div className="relative bg-gray-900 rounded-full p-1">
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <ChartBarSquareIcon className="h-20 w-20 text-gray-600" />
                </div>
              </div>
              {/* Status indicator */}
              <div className="absolute bottom-1 right-1 w-4 h-4 bg-yellow-400 rounded-full border-2 border-gray-900"></div>
            </div>
              
            {/* Model Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="mb-1 text-xs text-gray-400 uppercase tracking-wider">Status</div>
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 mb-2">
                Crypto Price Predictor v0.3
              </h2>
              <div className="flex flex-wrap justify-center md:justify-start gap-3 text-xs">
                <div className="px-2 py-1 bg-black bg-opacity-40 rounded border border-gray-700">
                  <span className="text-gray-300">Latest Update</span>
                  <span className="ml-2 text-pink-400">April 10, 2025</span>
                </div>
                <div className="px-2 py-1 bg-black bg-opacity-40 rounded border border-gray-700">
                  <span className="text-gray-300">Total Accuracy</span>
                  <span className="ml-2 text-pink-400">55%</span>
                </div>
              </div>
            </div>
              
            {/* Overall Progress */}
            <div className="flex flex-col items-center">
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Overall Progress</div>
              <div className="relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-lg opacity-70 blur"></div>
                <div className="relative px-8 py-3 bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-lg flex flex-col items-center justify-center">
                  <ArrowTrendingUpIcon className="h-6 w-6 text-pink-400 mb-1" />
                  <div className="font-bold text-2xl text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                    42%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Model Progress Cards - Similar to Stats Grid in Profile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {modelStats.map((stat) => (
            <div 
              key={stat.id}
              className="relative rounded-lg overflow-hidden transition-all duration-300 transform hover:scale-105"
              onMouseEnter={() => setActiveCard(stat.id)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} opacity-0 transition-opacity duration-300 ${activeCard === stat.id ? 'opacity-100 blur' : ''}`}></div>
              <div className="bg-gradient-to-br from-black to-gray-900 border border-gray-700 rounded-lg p-5 relative">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-400 uppercase tracking-wider">{stat.title}</div>
                  <stat.icon className="h-5 w-5 text-pink-400" />
                </div>
                <div className="text-sm text-gray-300 mt-1">{stat.description}</div>
                <div className="text-2xl font-bold mt-2 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                  {stat.progress}%
                </div>
                <div className="mt-3 h-1 w-full bg-gray-800 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${stat.color}`} style={{ width: `${stat.progress}%` }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Technical Performance Metrics */}
        <div className="mt-6 mb-6 p-6 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl">
          <div className="flex items-center mb-4">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Technical Metrics
            </h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
            <div className="p-4 bg-black bg-opacity-40 rounded-lg border border-gray-700">
              <div className="text-xs text-gray-400 uppercase">Inference Time</div>
              <div className="mt-1 text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                145ms
              </div>
              <div className="text-xs text-gray-500">Average processing time per prediction</div>
            </div>
            
            <div className="p-4 bg-black bg-opacity-40 rounded-lg border border-gray-700">
              <div className="text-xs text-gray-400 uppercase">F1 Score</div>
              <div className="mt-1 text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                0.53
              </div>
              <div className="text-xs text-gray-500">Balance between precision and recall</div>
            </div>
            
            <div className="p-4 bg-black bg-opacity-40 rounded-lg border border-gray-700">
              <div className="text-xs text-gray-400 uppercase">Parameters</div>
              <div className="mt-1 text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                8.3M
              </div>
              <div className="text-xs text-gray-500">Total trainable parameters</div>
            </div>
            
            <div className="p-4 bg-black bg-opacity-40 rounded-lg border border-gray-700">
              <div className="text-xs text-gray-400 uppercase">Training Time</div>
              <div className="mt-1 text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                4.5h
              </div>
              <div className="text-xs text-gray-500">On A100 GPU infrastructure</div>
            </div>
          </div>
        </div>

                {/* Upcoming Plans - Similar styling to Referral System box in Profile */}
                <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
                Next Development Steps
              </h2>
              <div className="h-px flex-grow ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent"></div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-gradient-to-br from-violet-500/20 to-pink-500/20 text-pink-400 border border-pink-500/30 mr-3">
                  1
                </div>
                <div>
                  <div className="text-white font-medium">Deploy LSTM as an inference endpoint</div>
                  <div className="text-sm text-gray-400 mt-1">Convert trained model to ONNX / TorchScript for production deployment</div>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-gradient-to-br from-violet-500/20 to-pink-500/20 text-pink-400 border border-pink-500/30 mr-3">
                  2
                </div>
                <div>
                  <div className="text-white font-medium">Connect to <code className="text-pink-400">/api/predict</code> endpoint</div>
                  <div className="text-sm text-gray-400 mt-1">Integrate model inference with real-time API for frontend consumption</div>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-gradient-to-br from-violet-500/20 to-pink-500/20 text-pink-400 border border-pink-500/30 mr-3">
                  3
                </div>
                <div>
                  <div className="text-white font-medium">Enable "Up / Down" decisions in real-time UI</div>
                  <div className="text-sm text-gray-400 mt-1">Add prediction indicators to market charts with confidence intervals</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Back to Lobby Button - Same style as CTA in Profile */}
        <div className="mt-8 flex justify-center">
          <Link
            to="/"
            className="px-8 py-3 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg flex items-center space-x-2 border border-gray-700 hover:border-pink-500 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2 text-pink-500" />
            <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-violet-400">
              Back to Lobby
            </span>
          </Link>
        </div>
      </div>
    </main>
  );
}